/**
 * ContentAudits — list this client's AI Content Discoverability™ audits and
 * create new ones. An audit crawls the client's OWN site (robots/llms/sitemap +
 * key pages) and scores whether AI can ingest, parse, and understand its content
 * across Accessibility, Structure, and Comprehensibility. Immutable history —
 * re-auditing a domain creates a new dated run (the before/after proof substrate).
 */
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { BenchmarkRun, ContentAudit } from '@aurora/shared';
import { api, ApiClientError } from '../api/client';

/** Map content-audit statuses onto the existing pill palette. */
export function contentPillClass(status: ContentAudit['status']): string {
  switch (status) {
    case 'pending':
      return 'pill pill-pending';
    case 'complete':
      return 'pill pill-complete';
    case 'failed':
      return 'pill pill-failed';
    default:
      return 'pill pill-processing';
  }
}

function parseCsvInput(raw: string, max: number): string[] {
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, max);
}

/** Parse one-per-line or comma-separated URLs. */
function parseUrlInput(raw: string, max: number): string[] {
  return raw
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, max);
}

export function ContentAudits(): JSX.Element {
  const navigate = useNavigate();
  const [audits, setAudits] = useState<ContentAudit[]>([]);
  const [benchmarks, setBenchmarks] = useState<BenchmarkRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [domain, setDomain] = useState('');
  const [keyUrls, setKeyUrls] = useState('');
  const [audience, setAudience] = useState('');
  const [competitors, setCompetitors] = useState('');
  const [benchmarkRunId, setBenchmarkRunId] = useState('');
  const [model, setModel] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const load = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const [page, benchPage] = await Promise.all([
        api.listContentAudits(),
        api.listBenchmarks().catch(() => null),
      ]);
      setAudits(page.items);
      if (benchPage) {
        setBenchmarks(benchPage.items.filter((b) => b.status === 'complete'));
      }
    } catch (e) {
      setError(
        e instanceof ApiClientError ? e.message : 'Failed to load audits',
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!companyName.trim() || !domain.trim()) {
      setFormError('Company name and domain are required');
      return;
    }
    setSubmitting(true);
    setFormError(null);
    try {
      const { run } = await api.createContentAudit({
        companyName: companyName.trim(),
        domain: domain.trim(),
        keyUrls: parseUrlInput(keyUrls, 20),
        audience: parseCsvInput(audience, 10),
        competitors: parseCsvInput(competitors, 8),
        ...(benchmarkRunId ? { benchmarkRunId } : {}),
        ...(model.trim() ? { model: model.trim() } : {}),
      });
      navigate(`/content-audits/${run.id}`);
    } catch (err) {
      setFormError(
        err instanceof ApiClientError
          ? err.message
          : 'Failed to create the audit',
      );
      setSubmitting(false);
    }
  };

  return (
    <div className="content-audits">
      <div className="page-head">
        <h1>AI Content Discoverability™ Audits</h1>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setShowForm((v) => !v)}
        >
          {showForm ? 'Close' : 'New audit'}
        </button>
      </div>

      {showForm && (
        <form className="card" onSubmit={onSubmit}>
          <div className="field">
            <label htmlFor="ca-company">Company name *</label>
            <input
              id="ca-company"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="e.g. Biomatik"
              required
            />
          </div>
          <div className="field">
            <label htmlFor="ca-domain">Domain to audit *</label>
            <input
              id="ca-domain"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="https://www.example.com"
              required
            />
          </div>
          <div className="field">
            <label htmlFor="ca-urls">
              Priority pages (one per line — product / application URLs to check
              first)
            </label>
            <textarea
              id="ca-urls"
              rows={3}
              value={keyUrls}
              onChange={(e) => setKeyUrls(e.target.value)}
              placeholder={
                'https://www.example.com/products\nhttps://www.example.com/applications'
              }
            />
          </div>
          <div className="field">
            <label htmlFor="ca-audience">
              Audience / buyer groups (comma-separated, optional)
            </label>
            <input
              id="ca-audience"
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
              placeholder="Academic researchers, Biotech/Pharma researchers"
            />
          </div>
          <div className="field">
            <label htmlFor="ca-competitors">
              Competitors (comma-separated, optional)
            </label>
            <input
              id="ca-competitors"
              value={competitors}
              onChange={(e) => setCompetitors(e.target.value)}
              placeholder="GenScript, IDT, Bio-Techne"
            />
          </div>
          <div className="field">
            <label htmlFor="ca-benchmark">
              Ground in a benchmark run (optional — explains WHY AI visibility
              is low at the content layer)
            </label>
            <select
              id="ca-benchmark"
              value={benchmarkRunId}
              onChange={(e) => setBenchmarkRunId(e.target.value)}
            >
              <option value="">None</option>
              {benchmarks.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.companyName} — {new Date(b.createdAt).toLocaleDateString()}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="ca-model">
              Auditor model (OpenRouter slug; empty = configured default)
            </label>
            <input
              id="ca-model"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              placeholder="openai/gpt-5.4-mini"
            />
          </div>
          {formError && <div className="form-error">{formError}</div>}
          <div className="head-actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting}
            >
              {submitting ? 'Creating…' : 'Run audit'}
            </button>
          </div>
        </form>
      )}

      {error && <div className="form-error">{error}</div>}
      {loading ? (
        <div className="muted centered">Loading audits…</div>
      ) : audits.length === 0 ? (
        <div className="empty-state muted">
          No content audits yet. Create one to see whether AI can actually
          ingest, parse, and understand a company&rsquo;s website — scored on
          Accessibility, Structure, and Comprehensibility.
        </div>
      ) : (
        <table className="leads-table">
          <thead>
            <tr>
              <th>Company</th>
              <th>Overall</th>
              <th>A / S / C</th>
              <th>Status</th>
              <th>Cost</th>
              <th>Created</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {audits.map((a) => (
              <tr key={a.id}>
                <td className="lead-name">{a.companyName}</td>
                <td>{a.overallScore ?? '—'}</td>
                <td>
                  {a.accessibilityScore ?? '—'} / {a.structureScore ?? '—'} /{' '}
                  {a.comprehensibilityScore ?? '—'}
                </td>
                <td>
                  <span className={contentPillClass(a.status)}>{a.status}</span>
                </td>
                <td>{a.usage ? `$${a.usage.cost.toFixed(2)}` : '—'}</td>
                <td>{new Date(a.createdAt).toLocaleDateString()}</td>
                <td>
                  <Link
                    to={`/content-audits/${a.id}`}
                    className="btn btn-small"
                  >
                    View report
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
